<script lang="ts">
    import AppLayout from '@/layouts/AppLayout.svelte';
    import { type BreadcrumbItem } from '@/types';
    import * as Card from '@/components/ui/card';
    import { Users, Briefcase, TrendingUp, Truck, UserCheck, Settings, Check } from 'lucide-svelte';
    import * as Tabs from '@/components/ui/tabs';
    import LeadStatusChart from '@/components/dashboard/LeadStatusChart.svelte';
    import { Input } from '@/components/ui/input';
    import { Button } from '@/components/ui/button';
    import { useForm } from '@inertiajs/svelte';

    type MonthlyData = {
        month: string;
        fullMonth: string;
        count: number;
        value: number;
    };

    type PeriodData = {
        count: number;
        value: number;
        previousCount: number;
        previousValue: number;
        countChange: number;
        valueChange: number;
        data: MonthlyData[];
    };

    type LeadChartData = {
        monthly: MonthlyData[];
        periods: Record<string, PeriodData>;
    };

    type Props = {
        stats: {
            customers: { total: number; recent: number };
            leads: { total: number; new: number; contacted: number; qualified: number };
            jobs: { total: number; active: number; completed: number; upcoming: number };
            vehicles: { total: number; available: number };
            employees: { total: number; active: number };
        };
        leadChartData?: {
            total_leads: LeadChartData;
            won_not_invoiced: LeadChartData;
            total_invoiced: LeadChartData;
            closed_lost: LeadChartData;
        };
        monthlyTarget?: number;
        isAdmin?: boolean;
    };

    let { stats, leadChartData, monthlyTarget = 0, isAdmin = false }: Props = $props();

    // Target editing state
    let isEditingTarget = $state(false);
    let targetForm = useForm({ value: monthlyTarget });

    function submitTarget() {
        $targetForm.patch(route('settings.monthly-target'), {
            preserveScroll: true,
            onSuccess: () => {
                isEditingTarget = false;
            },
        });
    }

    // Format currency for display
    function formatCurrency(value: number): string {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    }

    // Period selector state
    let selectedPeriod: 'monthly' | '3month' | '6month' | 'yearly' = $state('monthly');

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'Dashboard',
            href: '/dashboard',
        },
    ];
</script>

<svelte:head>
    <title>Dashboard</title>
</svelte:head>

<AppLayout {breadcrumbs}>
    <div class="space-y-6 p-6">
        <!-- Top Stats Grid -->
        <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <!-- Customers Card -->
            <Card.Root>
                <Card.Header class="flex flex-row items-center justify-between pb-2">
                    <Card.Title class="text-sm font-medium">Customers</Card.Title>
                    <Users class="h-4 w-4 text-muted-foreground" />
                </Card.Header>
                <Card.Content>
                    <div class="text-2xl font-bold">{stats.customers.total}</div>
                    <p class="text-xs text-muted-foreground">
                        +{stats.customers.recent} in last 30 days
                    </p>
                </Card.Content>
            </Card.Root>

            <!-- Leads Card -->
            <Card.Root>
                <Card.Header class="flex flex-row items-center justify-between pb-2">
                    <Card.Title class="text-sm font-medium">Leads</Card.Title>
                    <TrendingUp class="h-4 w-4 text-muted-foreground" />
                </Card.Header>
                <Card.Content>
                    <div class="text-2xl font-bold">{stats.leads.total}</div>
                    <p class="text-xs text-muted-foreground">
                        {stats.leads.new} new, {stats.leads.qualified} qualified
                    </p>
                </Card.Content>
            </Card.Root>

            <!-- Jobs Card -->
            <Card.Root>
                <Card.Header class="flex flex-row items-center justify-between pb-2">
                    <Card.Title class="text-sm font-medium">Jobs</Card.Title>
                    <Briefcase class="h-4 w-4 text-muted-foreground" />
                </Card.Header>
                <Card.Content>
                    <div class="text-2xl font-bold">{stats.jobs.total}</div>
                    <p class="text-xs text-muted-foreground">
                        {stats.jobs.active} active, {stats.jobs.upcoming} upcoming
                    </p>
                </Card.Content>
            </Card.Root>

            <!-- Vehicles Card -->
            <Card.Root>
                <Card.Header class="flex flex-row items-center justify-between pb-2">
                    <Card.Title class="text-sm font-medium">Vehicles</Card.Title>
                    <Truck class="h-4 w-4 text-muted-foreground" />
                </Card.Header>
                <Card.Content>
                    <div class="text-2xl font-bold">{stats.vehicles.total}</div>
                    <p class="text-xs text-muted-foreground">
                        {stats.vehicles.available} available
                    </p>
                </Card.Content>
            </Card.Root>
        </div>

        <!-- Lead Status Charts -->
        {#if leadChartData}
            <div class="space-y-4">
                <div class="flex flex-wrap items-center justify-between gap-4">
                    <div class="flex items-center gap-4">
                        <h2 class="text-lg font-semibold">Lead Performance</h2>
                        {#if isAdmin}
                            <div class="flex items-center gap-2">
                                {#if isEditingTarget}
                                    <Input
                                        type="number"
                                        bind:value={$targetForm.value}
                                        class="h-8 w-32"
                                        placeholder="Monthly target"
                                        min="0"
                                        step="100"
                                    />
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        class="h-8 w-8 p-0"
                                        onclick={submitTarget}
                                        disabled={$targetForm.processing}
                                    >
                                        <Check class="h-4 w-4" />
                                    </Button>
                                {:else}
                                    <button
                                        class="flex items-center gap-1 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                                        onclick={() => {
                                            $targetForm.value = monthlyTarget;
                                            isEditingTarget = true;
                                        }}
                                    >
                                        <Settings class="h-3 w-3" />
                                        <span>Target: {monthlyTarget > 0 ? formatCurrency(monthlyTarget) : 'Not set'}/mo</span>
                                    </button>
                                {/if}
                            </div>
                        {:else if monthlyTarget > 0}
                            <span class="text-xs text-muted-foreground">Target: {formatCurrency(monthlyTarget)}/mo</span>
                        {/if}
                    </div>
                    <Tabs.Root bind:value={selectedPeriod}>
                        <Tabs.List>
                            <Tabs.Trigger value="monthly">Current Month</Tabs.Trigger>
                            <Tabs.Trigger value="3month">3 Month</Tabs.Trigger>
                            <Tabs.Trigger value="6month">6 Month</Tabs.Trigger>
                            <Tabs.Trigger value="yearly">Yearly</Tabs.Trigger>
                        </Tabs.List>
                    </Tabs.Root>
                </div>

                <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <LeadStatusChart
                        title="Total Leads"
                        description="All leads received per period"
                        data={leadChartData.total_leads}
                        color="blue"
                        {selectedPeriod}
                    />
                    <LeadStatusChart
                        title="Won (Not Invoiced)"
                        description="Closed won & Booked leads awaiting invoice"
                        data={leadChartData.won_not_invoiced}
                        color="purple"
                        {selectedPeriod}
                    />
                    <LeadStatusChart
                        title="Total Invoiced"
                        description="Leads that did convert"
                        data={leadChartData.total_invoiced}
                        color="green"
                        {selectedPeriod}
                        {monthlyTarget}
                        showTargetProgress={true}
                    />
                    <LeadStatusChart
                        title="Closed Lost"
                        description="Leads that didn't convert & cancelled Jobs"
                        data={leadChartData.closed_lost}
                        color="gray"
                        {selectedPeriod}
                    />
                </div>
            </div>
        {/if}

        <!-- Detailed Stats Grid -->
        <div class="grid gap-4 md:grid-cols-2">
            <!-- Lead Status Breakdown -->
            <Card.Root>
                <Card.Header>
                    <Card.Title>Lead Pipeline</Card.Title>
                    <Card.Description>Current status of all leads</Card.Description>
                </Card.Header>
                <Card.Content class="space-y-4">
                    <div class="flex items-center justify-between">
                        <span class="text-sm font-medium">New</span>
                        <span class="text-sm text-muted-foreground">{stats.leads.new}</span>
                    </div>
                    <div class="flex items-center justify-between">
                        <span class="text-sm font-medium">Contacted</span>
                        <span class="text-sm text-muted-foreground">{stats.leads.contacted}</span>
                    </div>
                    <div class="flex items-center justify-between">
                        <span class="text-sm font-medium">Qualified</span>
                        <span class="text-sm text-muted-foreground">{stats.leads.qualified}</span>
                    </div>
                </Card.Content>
            </Card.Root>

            <!-- Job Status Breakdown -->
            <Card.Root>
                <Card.Header>
                    <Card.Title>Job Overview</Card.Title>
                    <Card.Description>Current job statistics</Card.Description>
                </Card.Header>
                <Card.Content class="space-y-4">
                    <div class="flex items-center justify-between">
                        <span class="text-sm font-medium">Active</span>
                        <span class="text-sm text-muted-foreground">{stats.jobs.active}</span>
                    </div>
                    <div class="flex items-center justify-between">
                        <span class="text-sm font-medium">Completed</span>
                        <span class="text-sm text-muted-foreground">{stats.jobs.completed}</span>
                    </div>
                    <div class="flex items-center justify-between">
                        <span class="text-sm font-medium">Upcoming (Next 7 Days)</span>
                        <span class="text-sm text-muted-foreground">{stats.jobs.upcoming}</span>
                    </div>
                </Card.Content>
            </Card.Root>

            <!-- Employees Card -->
            <Card.Root>
                <Card.Header>
                    <Card.Title class="flex items-center gap-2">
                        <UserCheck class="h-5 w-5" />
                        Employees
                    </Card.Title>
                    <Card.Description>Team overview</Card.Description>
                </Card.Header>
                <Card.Content class="space-y-4">
                    <div class="flex items-center justify-between">
                        <span class="text-sm font-medium">Total Employees</span>
                        <span class="text-sm text-muted-foreground">{stats.employees.total}</span>
                    </div>
                    <div class="flex items-center justify-between">
                        <span class="text-sm font-medium">Active</span>
                        <span class="text-sm text-muted-foreground">{stats.employees.active}</span>
                    </div>
                </Card.Content>
            </Card.Root>
        </div>


    </div>
</AppLayout>
